import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { toast } from 'react-toastify';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as adminActions from '../../actions/admin';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import { t } from '../../i18n';
import './units.scss';

interface UnitDetailed {
    id: number;
    name: string;
    articles_count: number;
    can_delete: boolean;
    created_at: string | null;
    updated_at: string | null;
}

interface Props {
    history: any;
    actions: any;
    adminActions: any;
    unitsDetailed: UnitDetailed[];
    loading: boolean;
    buttonLoader: boolean;
}

const Units = (props: Props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [search, setSearch] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState('');
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<UnitDetailed | null>(null);

    useEffect(() => {
        props.adminActions.getAllUnitsDetailed();
    }, []);

    const refresh = () => props.adminActions.getAllUnitsDetailed();

    const list = props.unitsDetailed || [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(u => u.name.toLowerCase().includes(q));
    }, [list, search]);

    const isInitialLoad = props.loading && list.length === 0;

    const handleStartEdit = (unit: UnitDetailed) => {
        setEditingId(unit.id);
        setEditingName(unit.name);
    };

    const handleSaveEdit = (unit: UnitDetailed) => {
        const name = editingName.trim();
        if (!name) {
            toast.error(t('admin.units.toastFillName'));
            return;
        }
        if (name === unit.name) {
            setEditingId(null);
            return;
        }
        props.adminActions.editUnit(unit.id, { name }).then(() => {
            setEditingId(null);
            setEditingName('');
            refresh();
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const handleDelete = (unit: UnitDetailed) => {
        if (!unit.can_delete) {
            toast.error(t('admin.units.toast.hasArticles'));
            return;
        }
        setDeleteConfirm(unit);
    };

    const confirmDelete = () => {
        if (!deleteConfirm) return;
        const id = deleteConfirm.id;
        props.adminActions.deleteUnit(id).then(() => {
            setDeleteConfirm(null);
            refresh();
        });
    };

    const handleStartCreate = () => {
        setCreating(true);
        setNewName('');
    };

    const handleSaveCreate = () => {
        const name = newName.trim();
        if (!name) {
            toast.error(t('admin.units.toastFillName'));
            return;
        }
        props.adminActions.createUnit({ name }).then(() => {
            setCreating(false);
            setNewName('');
            refresh();
        });
    };

    const handleCancelCreate = () => {
        setCreating(false);
        setNewName('');
    };

    const renderCheckIcon = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );

    const renderCloseIcon = (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );

    return (
        <div className="unit-page">
            <MikaPageHeader
                title={<FormattedMessage id="admin.units.pageTitle" defaultMessage="Njësitë" />}
                subtitle={fm('admin.units.pageSubtitle', 'Menaxho njësitë matëse të artikujve')}
                actions={
                    <button
                        type="button"
                        className="unit-btn unit-btn--primary"
                        onClick={handleStartCreate}
                        disabled={creating || props.buttonLoader}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5v14" />
                        </svg>
                        <FormattedMessage id="admin.units.createBtn" defaultMessage="Krijo njësi" />
                    </button>
                }
            />

            <div className="unit-toolbar">
                <div className="unit-search">
                    <svg className="unit-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        placeholder={fm('admin.units.searchPlaceholder', 'Kërko njësi...')}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <div className="unit-toolbar__count">
                    <FormattedMessage
                        id="admin.units.countTotal"
                        defaultMessage="{count, plural, one {# njësi} other {# njësi}}"
                        values={{ count: filtered.length }}
                    />
                </div>
            </div>

            <div className="unit-list">
                <div className="unit-list__header">
                    <div className="unit-list__col unit-list__col--name">
                        <FormattedMessage id="admin.units.col.name" defaultMessage="Njësia" />
                    </div>
                    <div className="unit-list__col unit-list__col--num">
                        <FormattedMessage id="admin.units.col.articles" defaultMessage="Artikuj" />
                    </div>
                    <div className="unit-list__col unit-list__col--actions" />
                </div>

                {isInitialLoad ? (
                    [0, 1, 2, 3].map(i => (
                        <div key={`sk-${i}`} className="unit-row unit-row--skeleton">
                            <div className="unit-row__cell unit-row__cell--name">
                                <div className="unit-skeleton unit-skeleton--icon" />
                                <div className="unit-skeleton unit-skeleton--name" />
                            </div>
                            <div className="unit-row__cell unit-row__cell--num"><div className="unit-skeleton unit-skeleton--num" /></div>
                            <div className="unit-row__cell unit-row__cell--actions" />
                        </div>
                    ))
                ) : (
                    <>
                        {filtered.length === 0 && !creating && (
                            <div className="unit-empty">
                                {search.trim() ? (
                                    <FormattedMessage id="admin.units.empty.search" defaultMessage="Asnjë njësi nuk përputhet me kërkimin." />
                                ) : (
                                    <FormattedMessage id="admin.units.empty.list" defaultMessage="Nuk ka njësi. Krijoni një të re për të filluar." />
                                )}
                            </div>
                        )}

                        {filtered.map(unit => {
                            const isEditing = editingId === unit.id;

                            return (
                                <div
                                    key={unit.id}
                                    className={`unit-row ${isEditing ? 'unit-row--editing' : ''}`}
                                >
                                    <div className="unit-row__cell unit-row__cell--name">
                                        <div className="unit-row__icon">{unit.name?.slice(0, 3).toLowerCase() || '?'}</div>
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                className="unit-row__edit-input"
                                                value={editingName}
                                                onChange={e => setEditingName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSaveEdit(unit);
                                                    }
                                                    if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        handleCancelEdit();
                                                    }
                                                }}
                                                autoFocus
                                            />
                                        ) : (
                                            <div className="unit-row__name">{unit.name}</div>
                                        )}
                                    </div>
                                    <div className="unit-row__cell unit-row__cell--num">{unit.articles_count}</div>
                                    <div className="unit-row__cell unit-row__cell--actions">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="unit-icon-btn unit-icon-btn--success"
                                                    onClick={() => handleSaveEdit(unit)}
                                                    title={fm('common.action.save', 'Ruaj')}
                                                    disabled={props.buttonLoader}
                                                >
                                                    {renderCheckIcon}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="unit-icon-btn"
                                                    onClick={handleCancelEdit}
                                                    title={fm('common.action.cancel', 'Anulo')}
                                                >
                                                    {renderCloseIcon}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="unit-icon-btn"
                                                    onClick={() => handleStartEdit(unit)}
                                                    title={fm('common.action.edit', 'Ndrysho')}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="unit-icon-btn unit-icon-btn--danger"
                                                    onClick={() => handleDelete(unit)}
                                                    title={unit.can_delete ? fm('common.action.delete', 'Fshi') : fm('admin.units.toast.hasArticles', 'Nuk mund ta fshish — ka artikuj të lidhur')}
                                                    disabled={!unit.can_delete}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6" />
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                    </svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {creating ? (
                            <div className="unit-add-row unit-add-row--editing">
                                <div className="unit-row__cell unit-row__cell--name">
                                    <div className="unit-row__icon unit-row__icon--add">+</div>
                                    <input
                                        type="text"
                                        className="unit-row__edit-input"
                                        value={newName}
                                        placeholder={fm('admin.units.placeholderName', 'Emri i njësisë...')}
                                        onChange={e => setNewName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSaveCreate();
                                            }
                                            if (e.key === 'Escape') {
                                                e.preventDefault();
                                                handleCancelCreate();
                                            }
                                        }}
                                        autoFocus
                                    />
                                </div>
                                <div className="unit-row__cell unit-row__cell--actions">
                                    <button
                                        type="button"
                                        className="unit-icon-btn unit-icon-btn--success"
                                        onClick={handleSaveCreate}
                                        title={fm('common.action.create', 'Krijo')}
                                        disabled={props.buttonLoader}
                                    >
                                        {renderCheckIcon}
                                    </button>
                                    <button
                                        type="button"
                                        className="unit-icon-btn"
                                        onClick={handleCancelCreate}
                                        title={fm('common.action.cancel', 'Anulo')}
                                    >
                                        {renderCloseIcon}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="unit-add-row" onClick={handleStartCreate}>
                                <div className="unit-row__icon unit-row__icon--add">+</div>
                                <div className="unit-add-row__label">
                                    <FormattedMessage id="admin.units.addNew" defaultMessage="Krijo njësi të re" />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <MikaConfirmModal
                isOpen={deleteConfirm !== null}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={confirmDelete}
                title={fm('admin.units.deleteTitle', 'Fshi njësinë')}
                message={intl.formatMessage(
                    { id: 'admin.units.deleteConfirm', defaultMessage: 'A jeni të sigurt që doni të fshini {name}?' },
                    { name: '"' + (deleteConfirm?.name || '') + '"' }
                )}
                confirmLabel={fm('common.action.delete', 'Fshi')}
                confirmColor="danger"
            />
        </div>
    );
};

function mapGlobalStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        loading: state.app.loading,
        buttonLoader: state.app.buttonLoader,
        unitsDetailed: (state.admin as any).unitsDetailed || [],
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
    };
}

export default withRouter(connect(mapGlobalStateToProps, mapDispatchToProps)(Units as any));
